import { Controller } from '@hotwired/stimulus';
import MarkdownIt from 'markdown-it';

// Stimulus controller for rendering streamed AI message content as Markdown.
// sql-evidence blocks render server-side into a hidden store (see
// ProspectAssistant::EvidenceBlock); inlineEvidence() swaps each placeholder
// for its stored markup after every render.
// Optionally consumes the <p> markdown-it wraps a standalone placeholder in -- <details> can't nest inside one.
const EVIDENCE_PLACEHOLDER = /(?:<p>\s*)?⁣prospect-evidence:(\d+)⁣(?:\s*<\/p>)?/g;

export default class extends Controller {
  static targets = ['content', 'raw'];

  connect() {
    this.md = new MarkdownIt();
    this.observeStreaming();
    this.format();
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
  }

  // ─── Observation ────────────────────────────────────────────────────────────

  observeStreaming() {
    this.observer = new MutationObserver(() => {
      // Debounce via rAF — coalesces rapid text-node mutations during streaming
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => this.format());
    });

    this.observer.observe(this.rawTarget, {
      characterData: true, // text node changes (streaming appends)
      childList: true, // new nodes
      subtree: false,
    });
  }

  // ─── Formatting ─────────────────────────────────────────────────────────────

  normalizeMarkdown(text) {
    return text
      .replace(/(?<!\n)##/g, '\n\n##') // headings
      .replace(/```json\b/g, '```') // strip known language tags
      .replace(/```([^\n])/g, '```\n$1') // newline after opening ```
      .replace(/^\s*\{[^}]*\}\s*/s, ''); // remove {"relevant_tables":[...]}
  }

  format() {
    const raw = this.rawContent();

    if (raw.trim() === '') return;

    const rendered = this.inlineEvidence(this.md.render(this.normalizeMarkdown(raw)));

    if (this.hasContentTarget) {
      // Only update the DOM if the output actually changed (avoids cursor flicker)
      if (this.contentTarget.innerHTML !== rendered) {
        this.contentTarget.innerHTML = rendered;
      }
    }
  }

  // Swaps each placeholder for its markup in the evidence store. An
  // unresolved placeholder is invisible and resolves on the next render.
  inlineEvidence(html) {
    return html.replace(EVIDENCE_PLACEHOLDER, (match, id) => {
      const stored = this.element.querySelector(`[data-evidence-id="${id}"]`);
      return stored ? stored.outerHTML : match;
    });
  }

  // Pull raw markdown from either the raw target or a data attribute
  rawContent() {
    if (this.hasRawTarget) {
      return this.rawTarget.textContent ?? '';
    }
    // Fallback: read from a data-message-formatter-raw-value attribute
    return this.element.dataset.messageFormatterRawValue ?? '';
  }
}
